import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { toast } from "../../../components/Toast/toast";
import { exportTable } from "../../../utils/exportTable";
import { btn } from "../../../components/Button/buttonStyles";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "0.00";

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const COVERAGE_CLS = {
  "Fully covered": "text-green-600 font-medium",
  "Partially covered": "text-amber-600 font-medium",
  "Not covered": "text-danger-500 font-medium",
};

const getCoverage = (inv) => {
  const due = Number(inv.due_amount || 0);
  const paid = Number(inv.paid_amount || 0);
  const total = Number(inv.total || 0);
  if (total === 0) return "Not covered";
  if (due <= 0) return "Fully covered";
  if (paid > 0) return "Partially covered";
  return "Not covered";
};

const HEADERS = [
  "Code",
  "Linked payments",
  "Payment coverage",
  "Total",
  "Write off amount",
  "Paid amount",
  "Due amount",
  "Currency",
  "Issued at",
  "Due at",
  "Action",
];

// Mirrors HEADERS above, minus the action column.
const EXPORT_COLUMNS = [
  { header: "Code", value: (inv) => inv.invoice_code || "" },
  {
    header: "Linked payments",
    value: (inv) => inv.paymentMethod?.payment_method?.replace(/_/g, " ") || "",
  },
  { header: "Payment coverage", value: (inv) => getCoverage(inv) },
  { header: "Total", value: (inv) => fmtNum(inv.total) },
  { header: "Write off amount", value: (inv) => fmtNum(inv.write_off_amount) },
  { header: "Paid amount", value: (inv) => fmtNum(inv.paid_amount) },
  { header: "Due amount", value: (inv) => fmtNum(inv.due_amount) },
  { header: "Currency", value: (inv) => inv.currency?.currency?.code || "" },
  { header: "Issued at", value: (inv) => fmtDate(inv.issued_at) },
  { header: "Due at", value: (inv) => fmtDate(inv.due_at) },
];

const VendorInvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/vendor-invoices", { withCredentials: true })
      .then((res) => setInvoices(res.data.data || []))
      .catch(() => setError("Failed to fetch invoices"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Vendor Invoices</h1>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-xs text-gray-500">
            New invoices can be created on the{" "}
            <Link
              to="/vendor/receivables"
              className="text-brand-600 hover:underline font-medium"
            >
              Receivables
            </Link>{" "}
            page.
          </span>
          <button
            onClick={() => exportTable("my-invoices", EXPORT_COLUMNS, invoices)}
            className={btn("secondary", "sm")}
          >
            Export to Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}
      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{invoices.length}</span>{" "}
            items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1100px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {HEADERS.map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.length === 0 ? (
                    <tr>
                      <td
                        colSpan={HEADERS.length}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No invoices found.
                      </td>
                    </tr>
                  ) : (
                    invoices.map((inv, index) => {
                      const coverage = getCoverage(inv);
                      const coverageCls =
                        COVERAGE_CLS[coverage] ?? "text-gray-600";
                      const currCode = inv.currency?.currency?.code || "—";
                      const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";

                      return (
                        <tr
                          key={inv.id}
                          className={`${rowBg} hover:bg-brand-50 transition-colors`}
                        >
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/vendor/invoice/${inv.id}`}
                              className="text-brand-600 hover:text-brand-800 font-medium underline"
                            >
                              {inv.invoice_code}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs capitalize text-center">
                            {inv.paymentMethod?.payment_method?.replace(
                              /_/g,
                              " "
                            ) || "—"}
                          </td>
                          <td
                            className={`px-3 py-2 text-xs text-center ${coverageCls}`}
                          >
                            {coverage}
                          </td>
                          <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap">
                            {fmtNum(inv.total)}
                          </td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                            {fmtNum(inv.write_off_amount)}
                          </td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                            {fmtNum(inv.paid_amount)}
                          </td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                            {fmtNum(inv.due_amount)}
                          </td>
                          <td className="px-3 py-2 text-xs text-center">
                            {currCode}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                            {fmtDate(inv.issued_at)}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                            {fmtDate(inv.due_at)}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              title="Download invoice"
                              onClick={() => toast.error("Download coming soon")}
                              className="text-gray-400 hover:text-brand-600 transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 inline-block"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3"
                                />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VendorInvoicesPage;
